import Layout from '@/components/feature/Layout';
import { companyInfo } from '@/mocks/company';
import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

const whatsappMessage = encodeURIComponent('Olá! Estou no site da Maquinasdesoldageomembrana.com.br e gostaria de informações sobre máquinas de solda de geomembrana.');
const whatsappUrl = `https://wa.me/${companyInfo.whatsapp}?text=${whatsappMessage}`;

const schema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contato SoldaGeo',
  description: 'Entre em contato com a SoldaGeo para orçamentos e suporte técnico.',
  url: 'https://maquinadesoldageomembrana.com.br/contato',
  inLanguage: 'pt-BR',
};

export default function ContatoPage() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    // Client-side validation
    const name = (formData.get('name') as string || '').trim();
    const email = (formData.get('email') as string || '').trim();
    const phone = (formData.get('phone') as string || '').trim();
    const message = (formData.get('message') as string || '').trim();

    if (!name || !email || !phone || !message) {
      setStatus('error');
      setErrorMsg('Preencha todos os campos obrigatórios.');
      return;
    }

    if (message.length > 500) {
      setStatus('error');
      setErrorMsg('A mensagem deve ter no máximo 500 caracteres.');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    try {
      const payload = new URLSearchParams();
      payload.append('name', name);
      payload.append('email', email);
      payload.append('phone', phone);
      payload.append('message', message);

      const response = await fetch('https://readdy.ai/api/form/d9vqevuth95mubjtuaeg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const isSuccess = response.ok && parsed && parsed.code === 'OK';
      const isSpam = !isSuccess && (responseText.toLowerCase().includes('spam') || parsed?.meta?.message?.toLowerCase().includes('spam'));

      if (isSuccess) {
        setStatus('success');
        form.reset();
      } else if (isSpam) {
        setStatus('error');
        setErrorMsg('Erro de validação. Tente novamente mais tarde.');
      } else {
        setStatus('error');
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || 'Erro ao enviar. Tente novamente.';
        setErrorMsg(serverMsg);
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg('Erro de conexão. Verifique sua internet e tente novamente.');
    }
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <nav className="text-xs text-foreground-400 mb-6">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line mx-1" />
          <span className="text-foreground-600">Contato</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 uppercase leading-tight mb-4">
              Entre em Contato
            </h1>
            <p className="text-foreground-500 text-sm md:text-base mb-8">
              Tire dúvidas, solicite orçamentos ou fale com nossa equipe técnica. Respondemos em até 24h.
            </p>

            <div className="space-y-5 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                  <i className="ri-whatsapp-line text-primary-500 text-lg" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground-900 uppercase">WhatsApp</h3>
                  <p className="text-foreground-500 text-sm">{companyInfo.phone}</p>
                  <a href={whatsappUrl} target="_blank" rel="nofollow noreferrer" className="text-primary-600 hover:text-primary-700 text-xs font-medium mt-0.5 inline-block cursor-pointer">Abrir chat</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                  <i className="ri-mail-line text-primary-500 text-lg" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground-900 uppercase">E-mail</h3>
                  <p className="text-foreground-500 text-sm">{companyInfo.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                  <i className="ri-map-pin-line text-primary-500 text-lg" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground-900 uppercase">Endereço</h3>
                  <p className="text-foreground-500 text-sm">{companyInfo.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                  <i className="ri-time-line text-primary-500 text-lg" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground-900 uppercase">Atendimento</h3>
                  <p className="text-foreground-500 text-sm">Segunda a Sexta, 8h às 18h (Brasília)</p>
                </div>
              </div>
            </div>

            <iframe
              title="Localização SoldaGeo"
              src="https://www.google.com/maps?q=Rua+Hip%C3%B3lito+Greg%C3%B3rio+Pereira+127,+Canasvieiras,+Florian%C3%B3polis,+SC,+Brasil&output=embed"
              className="w-full h-64 rounded-2xl border border-background-200/70"
              loading="lazy"
              allowFullScreen
            />
          </div>

          <div className="bg-white rounded-2xl border border-background-200/70 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950 uppercase mb-6">Formulário de Orçamento</h2>

            {status === 'success' && (
              <div className="mb-5 rounded-xl bg-green-50 border border-green-200 text-green-800 p-4 text-sm flex items-start gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Mensagem enviada!</strong><br />
                  Nossa equipe responderá em breve. Se preferir, fale diretamente pelo WhatsApp.
                </div>
              </div>
            )}

            {status === 'error' && errorMsg && (
              <div className="mb-5 rounded-xl bg-red-50 border border-red-200 text-red-800 p-4 text-sm flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Erro ao enviar:</strong> {errorMsg}
                </div>
              </div>
            )}

            <form data-readdy-form="d9vqevuth95mubjtuaeg" onSubmit={handleSubmit}>
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute w-px h-px opacity-0 -z-10"
              />

              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1.5">Nome *</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                    placeholder="Seu nome completo"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1.5">E-mail *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1.5">Telefone / WhatsApp *</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                    placeholder="(XX) XXXXX-XXXX"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1.5">Mensagem *</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all resize-y"
                    placeholder="Descreva seu projeto, tipo de material, espessura, área e prazo..."
                  />
                  <p className="text-foreground-400 text-xs mt-1">Máximo 500 caracteres</p>
                </div>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-bold text-sm px-6 py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap uppercase tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'submitting' ? (
                    <><i className="ri-loader-4-line animate-spin" /> Enviando...</>
                  ) : (
                    <><i className="ri-send-plane-line" /> Enviar Orçamento</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
}